import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Scale, AlertTriangle, Server } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const MentionsLegales = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Building2,
      title: t('mentions.s1.title', "1. Éditeur du site"),
      content: (
        <div className="space-y-2">
          <p>{t('mentions.s1.p1', "Le site ")}<strong className="text-auxis-charcoal">AUXIS</strong>{t('mentions.s1.p2', " est édité par la société AUXIS.")}</p>
          <ul className="space-y-2 mt-4">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-auxis-gold mt-2 shrink-0" />
              <span><strong>{t('mentions.s1.siege', "Siège social :")}</strong> {t('mentions.s1.siegeValue', "Lot II A 28 PZ Amboditsiry, Antananarivo, Madagascar")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-auxis-gold mt-2 shrink-0" />
              <span><strong>{t('mentions.s1.email', "Email :")}</strong> {t('mentions.s1.emailValue', "services@auxis.mg")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-auxis-gold mt-2 shrink-0" />
              <span><strong>{t('mentions.s1.tel', "Téléphone :")}</strong> {t('mentions.s1.telValue', "+261 34 08 866 86")}</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      icon: Scale,
      title: t('mentions.s2.title', "2. Propriété intellectuelle"),
      content: t('mentions.s2.content', "L'ensemble de ce site relève de la législation malgache et internationale sur le droit d'auteur et la propriété intellectuelle. Tous les droits de reproduction sont réservés, y compris pour les documents téléchargeables et les représentations iconographiques et photographiques.")
    },
    {
      icon: AlertTriangle,
      title: t('mentions.s3.title', "3. Limitation de responsabilité"),
      content: t('mentions.s3.content', "AUXIS s'efforce d'assurer au mieux de ses possibilités, l'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, AUXIS décline toute responsabilité pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur le site.")
    },
    {
      icon: Server,
      title: t('mentions.s4.title', "4. Hébergement"),
      content: t('mentions.s4.content', "Ce site est hébergé par InMotion Hosting.")
    }
  ];

  return (
    <div className="min-h-screen bg-auxis-off-white dark:bg-auxis-charcoal py-20 px-4 sm:px-6 lg:px-8 font-quicksand relative overflow-hidden transition-colors duration-300">
      {/* Background with image */}
      <div className="absolute top-0 left-0 w-full h-96 -z-0 overflow-hidden rounded-b-[3rem] md:rounded-b-[5rem]">
        <div className="absolute inset-0 bg-auxis-charcoal" />
      </div>
      <div className="absolute top-20 left-10 w-64 h-64 bg-auxis-gold/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex justify-between items-center"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-auxis-gold transition-colors duration-300 font-medium group"
          >
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-auxis-gold/20 transition-colors">
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            </div>
            {t('mentions.backHome', "Retour à l'accueil")}
          </Link>

          <button
            onClick={() => i18n.changeLanguage(isEng ? 'fr' : 'en')}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-auxis-gold/20 text-white/80 hover:text-auxis-gold transition-all duration-300 font-medium border border-white/10 hover:border-auxis-gold/30"
          >
            <span className="uppercase text-sm tracking-wider">{isEng ? 'EN' : 'FR'}</span>
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white dark:bg-auxis-charcoal/90 dark:backdrop-blur-xl rounded-3xl shadow-soft-xl p-8 md:p-12 lg:p-16 border border-gray-100 dark:border-white/10"
        >
          <div className="flex items-center gap-4 mb-10 pb-8 border-b border-gray-100 dark:border-white/10">
            <div className="w-14 h-14 rounded-2xl bg-auxis-gold/10 flex items-center justify-center shrink-0">
              <Scale className="w-7 h-7 text-auxis-gold" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-auxis-charcoal dark:text-white tracking-tight">
                {t('mentions.title', "Mentions Légales")}
              </h1>
              <p className="text-auxis-charcoal/50 dark:text-white/50 mt-2 text-sm font-medium">
                {t('mentions.subtitle', "Dernière mise à jour :")} {isEng ? new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.section
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1 w-8 h-8 rounded-lg bg-gray-50 dark:bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-auxis-gold/10 group-hover:text-auxis-gold transition-colors duration-300">
                    <section.icon size={16} className="text-auxis-charcoal/40 dark:text-white/40 group-hover:text-auxis-gold transition-colors" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-auxis-charcoal dark:text-white mb-3 group-hover:text-auxis-gold transition-colors duration-300">
                      {section.title}
                    </h2>
                    <div className="text-auxis-charcoal/70 dark:text-white/70 leading-relaxed text-lg">
                      {section.content}
                    </div>
                  </div>
                </div>
              </motion.section>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MentionsLegales;